import { httpClient } from '@services/HttpClient';
import type {
  BulkImportResponse,
  IntrospectResponse,
  ImportPreviewRow,
  ColumnMappingConfig,
  DefaultValuesConfig,
} from '../types/index.js';

export interface PreviewMappedParams {
  fileId?: string | undefined;
  file?: File | undefined;
  sheetName?: string | undefined;
  headerRowIndex?: number | undefined;
  columnMapping: ColumnMappingConfig;
  defaultValues?: DefaultValuesConfig | undefined;
  overrides?: Record<number, Record<string, unknown>> | undefined;
}

export interface ImportMappedParams extends PreviewMappedParams {
  skipFailedRows?: boolean | undefined;
}

export const bulkImportService = {
  async downloadTemplate(): Promise<Blob> {
    const { data } = await httpClient.get<Blob>('/api/v1/bulk/template', {
      responseType: 'blob',
    });
    return data;
  },

  /** Introspects uploaded file (.xlsx, .xls, .csv) and returns sheets, headers, sample values & auto-mappings */
  async introspectFile(file: File): Promise<IntrospectResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await httpClient.post<IntrospectResponse>('/api/v1/bulk/introspect', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return data;
  },

  /** Dry-run validation with column mapping */
  async previewMapped(params: PreviewMappedParams): Promise<BulkImportResponse> {
    if (params.file) {
      const formData = new FormData();
      formData.append('file', params.file);
      formData.append('mapping', JSON.stringify(params.columnMapping));
      if (params.fileId) formData.append('fileId', params.fileId);
      if (params.sheetName) formData.append('sheetName', params.sheetName);
      if (params.headerRowIndex !== undefined) formData.append('headerRowIndex', String(params.headerRowIndex));
      if (params.defaultValues) formData.append('defaultValues', JSON.stringify(params.defaultValues));
      if (params.overrides) formData.append('overrides', JSON.stringify(params.overrides));

      const { data } = await httpClient.post<BulkImportResponse>('/api/v1/bulk/preview-mapped', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return data;
    }

    const { data } = await httpClient.post<BulkImportResponse>('/api/v1/bulk/preview-mapped', params, {
      timeout: 120000,
    });
    return data;
  },

  /** Real database commit with column mapping */
  async importMapped(params: ImportMappedParams): Promise<BulkImportResponse> {
    if (params.file) {
      const formData = new FormData();
      formData.append('file', params.file);
      formData.append('mapping', JSON.stringify(params.columnMapping));
      if (params.fileId) formData.append('fileId', params.fileId);
      if (params.sheetName) formData.append('sheetName', params.sheetName);
      if (params.headerRowIndex !== undefined) formData.append('headerRowIndex', String(params.headerRowIndex));
      if (params.defaultValues) formData.append('defaultValues', JSON.stringify(params.defaultValues));
      if (params.overrides) formData.append('overrides', JSON.stringify(params.overrides));
      formData.append('skipFailedRows', String(params.skipFailedRows ?? true));

      const { data } = await httpClient.post<BulkImportResponse>('/api/v1/bulk/import-mapped', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      return data;
    }

    const { data } = await httpClient.post<BulkImportResponse>('/api/v1/bulk/import-mapped', params, {
      timeout: 120000,
    });
    return data;
  },

  /** Generates Excel import/audit report */
  async exportReport(rowStatuses: ImportPreviewRow[]): Promise<Blob> {
    const { data } = await httpClient.post<Blob>(
      '/api/v1/bulk/export-report',
      { rowStatuses },
      { responseType: 'blob' },
    );
    return data;
  },

  /** Legacy dry-run fallback */
  async previewFile(file: File): Promise<BulkImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await httpClient.post<BulkImportResponse>('/api/v1/bulk/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return data;
  },

  /** Legacy import fallback */
  async uploadFile(file: File): Promise<BulkImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await httpClient.post<BulkImportResponse>('/api/v1/bulk/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return data;
  },
};
